import { types, getRoot } from 'mobx-state-tree';
import { parseISO } from 'date-fns';

import { ROUTES, USER_ROLES, AVAILABLE_FOR_WORK_STATUS, months } from 'src/constants';
import { IResourcesStore } from '../ResourcesStore';
import { ExperienceModel } from './ExperienceModel';
import { EducationModel } from './EducationModel';

const UserModel = types
  .model({
    id: types.maybeNull(types.number),
    email: types.maybeNull(types.string),
    role: types.maybeNull(types.enumeration('role', Object.values(USER_ROLES))),
    permission: types.maybeNull(types.string),
    admin: types.maybeNull(types.boolean),
    sainoo_team: types.maybeNull(types.boolean),
    is_admin_logined_as: types.maybeNull(types.number),
    hiring_teams: types.maybeNull(types.boolean),
    is_employer_confirmed: types.maybeNull(types.boolean),
    photo_url: types.maybeNull(types.string),
    photo_thumbnail_url: types.maybeNull(types.string),
    photo_filename: types.maybeNull(types.string),
    first_name: types.maybeNull(types.string),
    last_name: types.maybeNull(types.string),
    phone: types.maybeNull(types.string),
    additional_emails: types.maybeNull(types.array(types.frozen())),
    resume_url: types.maybeNull(types.string),
    resume_updated_at: types.maybeNull(types.string),
    resume_filename: types.maybeNull(types.string),
    country: types.maybeNull(types.string),
    available_for_work_status: types.maybeNull(types.enumeration(Object.values(AVAILABLE_FOR_WORK_STATUS))),
    linkedin: types.maybeNull(types.string),
    website: types.maybeNull(types.string),
    gender: types.maybeNull(types.string),
    general: types.maybeNull(
      types.frozen({
        years: types.string,
        days: types.number,
        themes: types.frozen(),
        preference_themes: types.array(types.frozen()),
        focuses: types.array(types.frozen())
      })
    ),
    candidate_section: types.maybeNull(types.boolean),
    total_completion: types.maybeNull(types.number),
    experiences: types.maybeNull(types.array(ExperienceModel)),
    degrees: types.maybeNull(types.array(EducationModel)),
    languages: types.maybeNull(types.array(types.frozen())),
    work_places: types.maybeNull(types.array(types.frozen())),
    notifications_count: types.maybeNull(types.number),
    admin_notifications_count: types.maybeNull(types.number),
    admin_job_board_notifications_count: types.maybeNull(types.number),
    company: types.maybeNull(
      types.frozen<{
        id: number;
        name?: string;
        color?: string;
        website?: string;
        state?: string;
        country?: string;
        city?: string;
        street?: string;
        postcode?: string;
        lat?: number;
        lon?: number;
        show_interest?: boolean;
        show_map?: boolean;
        address?: string;
        interest_url?: string;
        slug?: string;
        license?: string;
        industry?: string;
        logo_url?: string;
        cover_picture_url?: string;
      }>()
    )
  })
  .views(self => ({
    get fullName() {
      return [self.first_name, self.last_name].join(' ');
    }
  }));

export const GeneralUserModel = types
  .model('GeneralUserModel', {
    user: types.maybeNull(UserModel)
  })
  .views(self => ({
    get userProfileFormsCompletenessStatus() {
      const generalFields = ['email', 'first_name', 'last_name', 'gender', 'phone', 'languages'];
      return {
        general: !generalFields.some(field => !self.user[field]),
        resume: Boolean(self.user.resume_filename),
        employment: Boolean(self.user.experiences?.length),
        education: Boolean(self.user.degrees?.length)
      };
    },
    get incompleteUserProfileRoute() {
      const incompleteFormKey = Object.keys(this.userProfileFormsCompletenessStatus).filter(
        key => !this.userProfileFormsCompletenessStatus[key]
      )[0];

      return incompleteFormKey
        ? ROUTES.candidatesRoutes.profile.edit[incompleteFormKey]
        : ROUTES.candidatesRoutes.profile.base;
    },
    get initialUserInfo() {
      const rootStore = getRoot<{ resourcesStore: IResourcesStore }>(self);
      const { resourcesStore } = rootStore;
      if (!self.user) return null;
      return {
        ...self.user,
        languages_attributes: self.user.languages?.toJSON(),
        experiences_attributes: self.user.experiences?.toJSON().map(experience => {
          const startDate = experience.start && parseISO(experience.start);
          const startProps =
            startDate instanceof Date
              ? {
                  month: months[startDate.getMonth()],
                  year: String(startDate.getFullYear())
                }
              : { month: '', year: '' };
          const endDate = experience.finish && parseISO(experience.finish);
          const endProps = experience.is_current_job
            ? { month: 'Current', year: '' }
            : endDate instanceof Date
            ? {
                month: months[endDate.getMonth()],
                year: String(endDate.getFullYear())
              }
            : { month: '', year: '' };

          return {
            ...experience,
            startDate: startProps,
            endDate: endProps,
            main_theme: resourcesStore.themesById?.allThemes[experience.main_theme_id],
            sub_theme: resourcesStore.themesById?.allThemes[experience.sub_theme_id],
            skills_attributes: experience.focuses
          };
        }),
        degrees_attributes: self.user.degrees?.toJSON().map(degree => {
          const startDate = parseISO(degree.start);
          const startProps =
            startDate instanceof Date
              ? {
                  month: months[startDate.getMonth()],
                  year: String(startDate.getFullYear())
                }
              : { month: '', year: '' };
          const endDate = parseISO(degree.grade);
          const endProps =
            endDate instanceof Date
              ? {
                  month: months[endDate.getMonth()],
                  year: String(endDate.getFullYear())
                }
              : { month: '', year: '' };
          return {
            ...degree,
            startDate: startProps,
            endDate: endProps
          };
        })
      };
    }
  }));
